import { create } from 'zustand';
import type { Argon2Params, Bytes, UnlockedVault } from '@glob/crypto';

// UnlockedVault carries: vk, rsaPrivateKey, rsaPublicKey,
// mlkemPrivateKey (Bytes), mlkemPublicKey (Bytes), unwrapIk, wrapIk.
// The ML-KEM halves are required for unwrapping shares received from
// other users (see Task 6b/6c sharing flows) and for being a valid recipient.

/**
 * Read-only key-derivation metadata cached at unlock time.
 *
 * Holds the salts + Argon2 params + the WK envelope so the settings page can
 * verify the current passphrase (derive old WK → unwrap current WK envelope)
 * before performing a passphrase rotation. None of these are VK material —
 * they are server-side persistent values; caching them in the tab adds no
 * sensitivity beyond what the login round-trip already exposed.
 *
 * Cleared on lock(); rebuilt on next unlock.
 */
export interface VaultMeta {
  readonly passphraseSalt: Bytes;
  readonly passphraseArgon2Params: Argon2Params;
  readonly wkEnvelope: Bytes;
  readonly recoverySalt: Bytes;
  readonly recoveryArgon2Params: Argon2Params;
}

interface VaultState {
  status: 'locked' | 'unlocking' | 'unlocked';
  vault: UnlockedVault | null;
  userUuid: string | null;
  meta: VaultMeta | null;
  setUnlocked: (vault: UnlockedVault, userUuid: string, meta?: VaultMeta) => void;
  setUnlocking: () => void;
  /** Update cached meta after a passphrase/recovery rotation. */
  updateMeta: (patch: Partial<VaultMeta>) => void;
  /**
   * Lock the vault and clear all VK-adjacent state.
   * @param opts.broadcast set to false when reacting to a sibling-tab lock
   *                       event to avoid a feedback loop.
   */
  lock: (opts?: { broadcast?: boolean }) => void;
}

// BroadcastChannel for cross-tab lock synchronisation. Same-origin tabs all
// hear each other; this is fine — the vault is per-origin anyway. Older
// browsers / non-DOM test environments may not expose BroadcastChannel; in
// that case multi-tab sync is a no-op (the per-tab idle timer still fires).
const LOCK_CHANNEL_NAME = 'glob-vault-lock';
const lockChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(LOCK_CHANNEL_NAME) : null;

interface LockMessage {
  type: 'locked';
}

export const useVaultStore = create<VaultState>((set) => ({
  status: 'locked',
  vault: null,
  userUuid: null,
  meta: null,
  setUnlocked: (vault, userUuid, meta) =>
    set({ status: 'unlocked', vault, userUuid, meta: meta ?? null }),
  setUnlocking: () => set({ status: 'unlocking' }),
  updateMeta: (patch) => set((s) => (s.meta ? { meta: { ...s.meta, ...patch } } : { meta: null })),
  lock: (opts) => {
    set({ status: 'locked', vault: null, userUuid: null, meta: null });
    if (opts?.broadcast !== false && lockChannel) {
      try {
        const msg: LockMessage = { type: 'locked' };
        lockChannel.postMessage(msg);
      } catch {
        // BroadcastChannel.postMessage can throw if the channel was closed
        // mid-tab-shutdown. Swallow — the lock itself already happened.
      }
    }
  },
}));

if (lockChannel) {
  lockChannel.addEventListener('message', (ev) => {
    const data = ev.data as LockMessage | null;
    if (data && data.type === 'locked') {
      // Don't re-broadcast: this would create an infinite loop across tabs.
      useVaultStore.getState().lock({ broadcast: false });
    }
  });
}
